import { PlanType } from 'modules/Const';

export interface FeatureDefinition {
  id: string;
  name: string;
  description: string;
  plans: readonly PlanType[];
  grandfatheredPlans?: readonly PlanType[];
  limits?: Record<string, number | string>;
}

export enum FeatureKeys {
  // Analytics Features
  ANALYTICS_LIFETIME = 'analytics_lifetime',
  ANALYTICS_EXTENDED = 'analytics_extended',
  ANALYTICS_ADVANCED = 'analytics_advanced',
  ANALYTICS_CUSTOM_DATE_RANGES = 'analytics_custom_date_ranges',
  ANALYTICS_EXPORT = 'analytics_export',
  
  // Campaign Features
  CAMPAIGN_AB_TESTING = 'campaign_ab_testing',
  CAMPAIGN_ADVANCED_TARGETING = 'campaign_advanced_targeting',
  CAMPAIGN_EDITING = 'campaign_editing',
  CAMPAIGN_FACEBOOK_PIXEL = 'campaign_facebook_pixel',
  
  // Website Features
  WEBSITE_PUBLISHING = 'website_publishing',
  WEBSITE_CUSTOM_THEMES = 'website_custom_themes',
  WEBSITE_PIXEL_TRACKING = 'website_pixel_tracking',
  WEBSITE_ANALYTICS_TRACKING = 'website_analytics_tracking',
  WEBSITE_CUSTOM_FONTS = 'website_custom_fonts',
  
  // AI Features
  AI_UNLIMITED_TASKS = 'ai_unlimited_tasks',
  AI_ADVANCED_TOOLS = 'ai_advanced_tools',
  
  // Presave Features
  PRESAVE_CREATION = 'presave_creation',
  PRESAVE_BOOSTS = 'presave_boosts',
  PRESAVE_FOREVER_SAVES = 'presave_forever_saves',
  
  // Team Features
  TEAM_COLLABORATION = 'team_collaboration',
  TEAM_MULTI_BRAND = 'team_multi_brand',
  TEAM_MEMBER_INVITE = 'team_member_invite',
}

export const FEATURES = {
  // Analytics Features
  [FeatureKeys.ANALYTICS_LIFETIME]: {
    id: FeatureKeys.ANALYTICS_LIFETIME,
    name: 'Lifetime Analytics',
    description: 'Access to historical analytics data beyond basic timeframes',
    plans: [PlanType.PRO, PlanType.GROWTH, PlanType.TEAM, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.ANALYTICS_ADVANCED]: {
    id: FeatureKeys.ANALYTICS_ADVANCED,
    name: 'Advanced Analytics',
    description: 'Access to Maestro AI insights, demographics, impact tracking, and detailed campaign analytics',
    plans: [PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },
  
  [FeatureKeys.ANALYTICS_EXTENDED]: {
    id: FeatureKeys.ANALYTICS_EXTENDED, 
    name: 'Extended Analytics',
    description: '30+ day analytics access',
    plans: [PlanType.LITE, PlanType.PRO, PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.LITE, PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.ANALYTICS_CUSTOM_DATE_RANGES]: {
    id: FeatureKeys.ANALYTICS_CUSTOM_DATE_RANGES,
    name: 'Custom Date Ranges',
    description: 'Custom date range selection for analytics',
    plans: [PlanType.PRO, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.ANALYTICS_EXPORT]: {
    id: FeatureKeys.ANALYTICS_EXPORT,
    name: 'Analytics Export',
    description: 'Export analytics data as CSV or images',
    plans: [PlanType.PRO, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  // Campaign Features
  [FeatureKeys.CAMPAIGN_AB_TESTING]: {
    id: FeatureKeys.CAMPAIGN_AB_TESTING,
    name: 'A/B Testing',
    description: 'Test multiple creative variations in campaigns',
    plans: [PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM],
    limits: {
      [PlanType.FREE]: 1,
      [PlanType.LITE]: 1,
      [PlanType.STARTER]: 1, 
      [PlanType.PRO]: 999,
      [PlanType.GROWTH]: 999,
      [PlanType.TEAM]: 999,
      [PlanType.TEAM_V2]: 999
    }
  },

  [FeatureKeys.CAMPAIGN_ADVANCED_TARGETING]: {
    id: FeatureKeys.CAMPAIGN_ADVANCED_TARGETING,
    name: 'Advanced Targeting',
    description: 'Access to advanced audience targeting options',
    plans: [PlanType.PRO, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.CAMPAIGN_EDITING]: {
    id: FeatureKeys.CAMPAIGN_EDITING,
    name: 'Campaign Editing',
    description: 'Edit active campaigns after launch',
    plans: [PlanType.PRO, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.CAMPAIGN_FACEBOOK_PIXEL]: {
    id: FeatureKeys.CAMPAIGN_FACEBOOK_PIXEL,
    name: 'Facebook Pixel Tracking',
    description: 'Connect Facebook Pixel for advanced conversion tracking and optimization',
    plans: [PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  // Website Features
  [FeatureKeys.WEBSITE_PUBLISHING]: {
    id: FeatureKeys.WEBSITE_PUBLISHING,
    name: 'Website Publishing',
    description: 'Publish and manage websites with custom domains',
    plans: [PlanType.STARTER, PlanType.GROWTH,  PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.FREE, PlanType.LITE, PlanType.PRO, PlanType.TEAM]
  },
  
  [FeatureKeys.WEBSITE_CUSTOM_THEMES]: {
    id: FeatureKeys.WEBSITE_CUSTOM_THEMES,
    name: 'Custom Website Themes',
    description: 'Access to premium website themes and customization',
    plans: [PlanType.LITE, PlanType.PRO, PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.LITE, PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.WEBSITE_ANALYTICS_TRACKING]: {
    id: FeatureKeys.WEBSITE_ANALYTICS_TRACKING,
    name: 'Website Analytics Tracking',
    description: 'Pixel tracking and analytics integration for websites',  
    plans: [PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },
  [FeatureKeys.WEBSITE_PIXEL_TRACKING]: {
    id: FeatureKeys.WEBSITE_PIXEL_TRACKING,
    name: 'Website Pixel Tracking',
    description: 'Pixel tracking and analytics integration for websites',  
    plans: [PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.LITE, PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.WEBSITE_CUSTOM_FONTS]: {
    id: FeatureKeys.WEBSITE_CUSTOM_FONTS,
    name: 'Custom Fonts',
    description: 'Access to premium font styles for websites',
    plans: [PlanType.LITE, PlanType.PRO, PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.LITE, PlanType.PRO, PlanType.TEAM]
  },

  // AI Features
  [FeatureKeys.AI_UNLIMITED_TASKS]: {
    id: FeatureKeys.AI_UNLIMITED_TASKS,
    name: 'Unlimited AI Tasks',
    description: 'Unlimited AI-powered marketing tasks and generation',
    plans: [PlanType.PRO, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.AI_ADVANCED_TOOLS]: {
    id: FeatureKeys.AI_ADVANCED_TOOLS,
    name: 'Advanced AI Tools',
    description: 'Access to advanced AI features like campaign generation',
    plans: [PlanType.PRO, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  // Presave Features
  [FeatureKeys.PRESAVE_CREATION]: {
    id: FeatureKeys.PRESAVE_CREATION,
    name: 'Pre-save Creation',
    description: 'Create and publish pre-save campaigns',
    plans: [PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.FREE, PlanType.LITE, PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.PRESAVE_BOOSTS]: {
    id: FeatureKeys.PRESAVE_BOOSTS,
    name: 'Presave Boosts',
    description: 'Advanced presave optimization features',
    plans: [PlanType.LITE, PlanType.PRO, PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.LITE, PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.PRESAVE_FOREVER_SAVES]: {
    id: FeatureKeys.PRESAVE_FOREVER_SAVES,
    name: 'Forever Saves',
    description: 'Forever save functionality for presave campaigns',
    plans: [PlanType.LITE, PlanType.PRO, PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.LITE, PlanType.PRO, PlanType.TEAM]
  },

  // Team Features
  [FeatureKeys.TEAM_COLLABORATION]: {
    id: FeatureKeys.TEAM_COLLABORATION,
    name: 'Team Collaboration',
    description: 'Invite team members and manage permissions',
    plans: [PlanType.GROWTH, PlanType.TEAM, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  },

  [FeatureKeys.TEAM_MULTI_BRAND]: {
    id: FeatureKeys.TEAM_MULTI_BRAND,
    name: 'Multi-Brand Management',
    description: 'Manage multiple brands from one dashboard',
    plans: [PlanType.FREE, PlanType.LITE, PlanType.PRO, PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.TEAM],
    limits: {
      [PlanType.FREE]: 3,
      [PlanType.STARTER]: 3,
      [PlanType.LITE]: 3,
      [PlanType.PRO]: 3,
      [PlanType.GROWTH]: 3,
      [PlanType.TEAM]: 'unlimited',
      [PlanType.TEAM_V2]: 'unlimited',
    }
  },

  [FeatureKeys.TEAM_MEMBER_INVITE]: {
    id: FeatureKeys.TEAM_MEMBER_INVITE,
    name: 'Team Member Invite',
    description: 'Invite members to your team',
    plans: [PlanType.GROWTH, PlanType.TEAM_V2],
    grandfatheredPlans: [PlanType.PRO, PlanType.TEAM]
  }
} as const;

export type FeatureId = keyof typeof FEATURES; 